<template>
  <section>
    <div class="row" v-if="preCheckoutData.user_place && !isEditing">
      <div class="col-12 p-0">
        <div class="card shadow-sm mb-0">
          <div class="card-body text-comment">
            <ul class="list-unstyled summary-list">
              <li>
                <i class="fal fa-user"></i>
                {{ preCheckoutData.name }}
              </li>
              <li class="pt-2">
                <i class="fal fa-phone"></i>
                {{ preCheckoutData.tel }}
              </li>
              <li class="pt-2">
                <i class="fal fa-home"></i>
                <span v-if="preCheckoutData.user_place">
                  {{ preCheckoutData.user_place.place_name }}<br>
                  {{ getFullAddress(preCheckoutData.user_place) }}
                </span>
                <span v-else>{{ preCheckoutData.address }}</span>
                <div class="pt-2" v-if="hasUnavailableServiceArea()">
                  <div class="text-danger">
                    <i class="fas fa-exclamation-circle"></i> {{ $t('message.booking.checkout_step.service_provider_cannot_travel') }}
                  </div>
                  <div class="text-danger pb-3">
                    {{ $t('message.booking.checkout_step.please_contact_admin') }} 
                    <a href="https://line.me/R/ti/p/%40hnk7011n" target="_blank" class="text-primary-link">Line @BeNeat</a>
                  </div>
                  <button class="btn btn-block btn-outline-info btn-sm" @click="openUnavailableServiceAreaModal()">
                    {{ $t('message.booking.checkout_step.view_more') }}
                  </button>
                </div>
              </li>
              <li class="pt-2" v-if="preCheckoutData.user_place && preCheckoutData.user_place.has_pet === 1">
                <i class="fas fa-paw"></i>
                {{ preCheckoutData.user_place.pet_remark }}
              </li>
            </ul>

            <!-- Warning no location of user place -->
            <div class="d-flex border border-warning justify-content-start align-items-top -bg-warning-soft rounded-all p-2" v-if="!hasPlaceLocation">
              <div>
                <i class="fal fa-exclamation-circle text-danger fa-lg"></i>
              </div>
              <div class="pl-2">
                {{ $t('message.booking.contact_step.no_location_warning') }}
              </div>
            </div>

            <div class="mt-3 text-right">
              <a href=""
                 class="text-primary-link"
                 @click.prevent="isEditing = true">{{ $t('message.booking.checkout_step.edit_data') }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="form" v-else>
      <form action="" data-vv-scope="form-address" @submit.prevent="formSubmit()">
        <div class="form-group">
          <label for="name">{{ $t('message.booking.contact_step.name') }}</label>
          <input type="text"
                 class="form-control input-jumbo"
                 :class="{'form-alert': errors.has('form-address.name')}"
                 id="name"
                 name="name"
                 ref="name"
                 :value="preCheckoutData.name"
                 @input="updatePreCheckoutData('name', $event)"
                 v-validate="'required'"
                 v-if="preCheckoutData">
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('form-address.name')">
            {{ $t('message.required_field') }}
          </div>
        </div>
        <!--/.form-group-->
        <div class="form-group" v-if="preBookingData && !user">
          <label for="email">{{ $t('message.booking.booking_step.email') }}</label>
          <input type="email"
                 class="form-control input-jumbo"
                 :class="{'form-alert': errors.has('form-address.email')}"
                 id="email"
                 name="email"
                 ref="email"
                 :value="preBookingData.email"
                 @input="updatePreBookingData('email', $event)"
                 v-validate="'required|email'"
                 v-if="preBookingData">
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('form-address.email')">
            {{ $t('message.required_field') }}
          </div>
        </div>
        <!--/.form-group-->
        <div class="form-group">
          <label for="tel">{{ $t('message.booking.contact_step.phone') }}</label>
          <input type="tel"
                 class="form-control input-jumbo"
                 :class="{'form-alert': errors.has('form-address.tel')}"
                 id="tel"
                 name="tel"
                 ref="tel"
                 :value="preCheckoutData.tel"
                 @input="updatePreCheckoutData('tel', $event)"
                 v-validate="{ required: true, min: 10, max: 15, regex: /^\+?[0-9-]+$/ }"
                 v-if="preCheckoutData">
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('form-address.tel')">
            {{ $t('message.required_field') }}
          </div>
        </div>
        <!--/.form-group-->
        <div class="my-4">
          <hr>
        </div>
        <!-- Divider -->
        <div class="form-group" v-if="preCheckoutData.user_place && preCheckoutData.user_place.id">
          <label for="placeName">
            {{ $t('message.my_listings.name') }}
          </label>
          <input type="text"
                 class="form-control input-jumbo"
                 :class="{'form-alert': errors.has('form-address.placeName')}"
                 id="placeName"
                 name="placeName"
                 v-model="preCheckoutData.user_place.place_name"
                 v-validate="'required'">
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('form-address.placeName')">
            {{ $t('message.required_field') }}
          </div>
        </div>
        <div class="form-group">
          <label for="placeType">{{ $t('message.booking.booking_step.place_size') }}</label>
          <div class="select-jumbo">
            <select :class="{'form-alert': errors.has('form-address.addressProvince')}"
                    v-model="preCheckoutData.user_place.place_size_id" 
                    v-validate="'required'"
                    id="placeType"
                    name="placeType">
              <option :value="null">{{ $t('message.booking.booking_step.select_place_size') }}</option>
              <option :value="sortedPlaceSizes[index].id" v-for="placeSize, index in sortedPlaceSizes">
                {{ placeSize | multiLangName }}
              </option>
            </select>
          </div>
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('form-address.placeType')">
            {{ $t('message.required_field') }}
          </div>
        </div>
        <div class="form-group">
          <label for="address">{{ $t('message.booking.contact_step.address') }}</label>
          <input type="text"
                 class="form-control input-jumbo"
                 :class="{'form-alert': errors.has('form-address.address')}"
                 id="address"
                 name="address"
                 ref="address"
                 :value="preCheckoutData.address"
                 @input="updatePreCheckoutData('address', $event)"
                 v-validate="'required'"
                 v-if="preCheckoutData">
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('form-address.address')">
            {{ $t('message.required_field') }}
          </div>
        </div>
        <!--/.form-group-->
        <div class="form-group">
          <label for="addressProvince">{{ $t('message.business.contact_province') }}</label>
          <div class="select-jumbo">
            <select v-model="selectedAddressProvinceId"
                    :class="{'form-alert': errors.has('form-address.addressProvince')}"
                    v-validate="'required'"
                    id="addressProvince"
                    name="addressProvince">
              <option :value="null">{{ $t('message.booking.contact_step.set_province') }}</option>
              <option :value="addressProvince.id" v-for="addressProvince, index in sortedProvinces">
                {{ addressProvince | multiLangName }}
              </option>
            </select>
          </div>
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('form-address.addressProvince')">
            {{ $t('message.required_field') }}
          </div>
        </div>
        <!--/.form-group-->
        <div class="row">
          <div class="col-6 pr-2 pl-0">
            <label for="district">{{ $t('message.booking.contact_step.district') }}</label>
            <div class="select-jumbo">
              <select v-model="selectedDistrictId"
                      :class="{'form-alert': errors.has('form-address.district')}"
                      v-validate="'required'"
                      id="district"
                      name="district">
                <option :value="null">{{ $t('message.booking.contact_step.set_district') }}</option>
                <option :value="district.id" v-for="district, index in sortedDistricts">
                  {{ district | multiLangName }}
                </option>
              </select>
            </div>
            <div class="mt-1 text-remark -status-alert" v-if="errors.has('form-address.district')">
              {{ $t('message.required_field') }}
            </div>
          </div>
          <div class="col-6 pl-2 pr-0">
            <label for="sub-district">{{ $t('message.booking.contact_step.sub_district') }}</label>
            <div class="select-jumbo">
              <select v-model="selectedSubDistrictId" id="sub-district">
                <option :value="null">{{ $t('message.booking.contact_step.set_sub_district') }}</option>
                <option :value="subDistrcict.id" v-for="subDistrcict, index in sortedSubDistricts">
                  {{ subDistrcict | multiLangName }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <!--/.form-group-->
        <div class="my-4">
          <hr>
        </div>
        <!-- Divider -->
        <div class="form-group">
          <label for="remark">{{ $t('message.booking.contact_step.remark') }}</label>
          <textarea id="remark"
                    rows="3"
                    ref="remark"
                    class="form-control textarea-jumbo"
                    :value="preCheckoutData.remark"
                    @input="updatePreCheckoutData('remark', $event)"
                    v-if="preCheckoutData"
                    :placeholder="$t('message.booking.contact_step.remark_placeholder')"></textarea>
        </div>
        <div>
          <label for="petRemark">{{ $t('message.booking.contact_step.pet_remark') }}</label>
          <div class="d-flex">
            <div class="flex-fill">
              <div class="exp">
                <div class="checkbox">
                  <input type="radio"
                         id="hasPet"
                         name="hasPet"
                         :value="1"
                         v-model="preCheckoutData.user_place.has_pet"
                         v-validate="'required'"/>
                  <label for="hasPet" class="mb-0">
                    <span><!-- This span is needed to create the "checkbox" element --></span>
                    {{ $t('message.confirm.yes') }}
                  </label>
                </div>
              </div>
            </div>
            <div class="flex-fill">
              <div class="exp">
                <div class="checkbox">
                  <input type="radio"
                         id="hasNoPet"
                         name="hasPet"
                         :value="0"
                         v-model="preCheckoutData.user_place.has_pet"
                         v-validate="'required'"/>
                  <label for="hasNoPet" class="mb-0">
                    <span><!-- This span is needed to create the "checkbox" element --></span>
                    {{ $t('message.confirm.no') }}
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-1 text-remark -status-alert" v-if="errors.has('form-address.hasPet')">
            {{ $t('message.required_field') }}
          </div>
          <div class="pt-3">
            <input type="text"
                   class="form-control input-jumbo"
                   :class="{'form-alert': errors.has('form-address.petRemark')}"
                   id="petRemark"
                   name="petRemark"
                   ref="petRemark"
                   :placeholder="$t('message.my_listings.pet_remark_placeholder')"
                   v-model="preCheckoutData.user_place.pet_remark"
                   v-validate="'required'"
                   v-if="preCheckoutData.user_place.has_pet && preCheckoutData.user_place.has_pet === 1">
            <div class="mt-1 text-remark -status-alert" v-if="errors.has('form-address.petRemark')">
              {{ $t('message.required_field') }}
            </div>
          </div>
        </div>

        <div class="pt-3">
          <label>{{ $t('message.booking.contact_step.location') }}</label>
          <div class="card my-0 border-divider"
               :class="{'border-danger': errors.has('form-address.location')}">
            <div class="card-body p-2" v-validate="{ required: !hasPlaceLocation }">
              <div class="d-flex justify-content-start align-items-center">
                <div class="flex-fill">
                  <span v-if="hasPlaceLocation">
                    <i class="fal fa-check-circle text-success fa-lg"></i>&nbsp;&nbsp;{{ $t('message.booking.contact_step.pinned_location') }}
                  </span>
                  <span v-else>
                    <i class="fal fa-exclamation-circle text-danger fa-lg"></i>&nbsp;&nbsp;{{ $t('message.booking.contact_step.unpinned_location') }}
                  </span>
                </div>
                <div>
                  <button class="btn btn-outline-info" @click="openPinlocationModal()">
                    {{ hasPlaceLocation ? $t('message.button.edit') : $t('message.button.add') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="text-remark -status-alert mt-1" v-if="errors.has('form-address.location')">
            {{ $t('message.required_field') }}
          </div>
        </div>

        <template v-if="preCheckoutData.user_place.id && isEditing">
          <button class="button button-large button-block -pink mt-3"
                  @click="saveMyPlace">{{ $t('message.booking.contact_step.save_my_place') }}
          </button>
        </template>
        <!--/.form-group-->
        <div class="row no-gutters mt-4">
          <div class="col-12 text-center" v-if="user && !state.isAddedUserPlace">
            <button class="button button-large button-block -pink"
                    @click="openSavePlaceModal">{{ $t('message.booking.contact_step.save_my_place') }}
            </button>
            <div class="mt-3">
              <span class="text-primary"><i class="far fa-lightbulb"></i>&nbsp;Tips!</span>
              {{ $t('message.booking.contact_step.save_my_place_tips') }}
            </div>
          </div>
        </div>
        <!-- Save my place button -->
      </form>
      <save-place-modal ref="savePlaceModal"/>
      <!-- Save My Place Modal -->
    </div>

    <!-- Unvailable service area modal for booking experience -->
    <unavailable-service-area-modal ref="unavailableServiceAreaModal"/>
    <!-- Pin user place location modal -->
    <pin-location-modal :settings="settings" ref="pinLocationModal" @savedLocation="clearLocationError" />
  </section>
</template>

<script>
  import { parse, format, isValidNumber } from 'libphonenumber-js'
  import { mapActions, mapGetters, mapState } from 'vuex'
  import PinLocationModal from '../components/PinLocationModal'
  import SavePlaceModal from '../components/SavePlaceModal'
  import UnavailableServiceAreaModal from '../../experience/components/UnavailableServiceAreaModal'
  import userPlaceHelpers from '../../../helpers/userPlaceHelpers'

  export default {
    inject: ['$validator'],
    components: {
      PinLocationModal,
      SavePlaceModal,
      UnavailableServiceAreaModal
    },
    data () {
      return {
        isEditing: false,
        sortBy: (this.$i18n.locale === 'th') ? 'name_th' : 'name_en',
        selectedAddressProvinceId: null,
        selectedDistrictId: null,
        selectedSubDistrictId: null,
        settings: {
          page: 'checkout',
          hideHeaderClose: false,
          noCloseOnBackdrop: false,
          okOnly: false
        }
      }
    },
    computed: {
      ...mapGetters({
        globalState: 'globalVal/getState',
        experience: 'experience/getExperience',
        preBookingData: 'booking/getPreBookingData',
        preCheckoutData: 'booking/getPreCheckoutData',
        state: 'booking/getState',
        user: 'auth/getUser',
        userPartner: 'partner/getUserPartner'
      }),
      ...mapState('addressProvince', ['addressProvinces', 'districts', 'subDistricts']),
      sortedPlaceSizes () {
        return this._.orderBy(this.globalState.placeSizes, ['id'], ['asc'])
      },
      sortedProvinces () {
        return this._.orderBy(this.addressProvinces, [this.sortBy], ['asc'])
      },
      sortedDistricts () {
        return this._.orderBy(this.districts, [this.sortBy], ['asc'])
      },
      sortedSubDistricts () {
        return this._.orderBy(this.subDistricts, [this.sortBy], ['asc'])
      },
      hasPlaceLocation () {
        let result = false
        const latitude = this.preCheckoutData.user_place.latitude ? Number(this.preCheckoutData.user_place.latitude) : null
        const longitude = this.preCheckoutData.user_place.longitude ? Number(this.preCheckoutData.user_place.longitude) : null
        if (this.preCheckoutData && this.preCheckoutData.user_place && latitude && longitude) {
          result = true
        }
        return result
      }
    },
    watch: {
      addressProvinces () {
        this.selectedAddressProvinceId = null
        if (this.preCheckoutData.user_place && this.preCheckoutData.user_place.address_province_id) {
          this.selectedAddressProvinceId = this.preCheckoutData.user_place.address_province_id
        }
      },
      districts () {
        this.selectedDistrictId = null
        if (this.preCheckoutData.user_place && this.preCheckoutData.user_place.district_id) {
          this.selectedDistrictId = this.preCheckoutData.user_place.district_id
        }
      },
      subDistricts () {
        this.selectedSubDistrictId = null
        if (this.preCheckoutData.user_place && this.preCheckoutData.user_place.sub_district_id) {
          this.selectedSubDistrictId = this.preCheckoutData.user_place.sub_district_id
        }
      },
      selectedAddressProvinceId () {
        this.fetchDistricts(this.selectedAddressProvinceId)
        this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {
          key: 'address_province_id',
          value: this.selectedAddressProvinceId
        })
        if (this.preCheckoutData.user_place) {
          this.preCheckoutData.user_place.address_province_id = this.selectedAddressProvinceId
        }
      },
      selectedDistrictId () {
        this.fetchSubDistricts(this.selectedDistrictId)
        this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {
          key: 'district_id',
          value: this.selectedDistrictId
        })
        if (this.preCheckoutData.user_place) {
          this.preCheckoutData.user_place.district_id = this.selectedDistrictId
        }
      },
      selectedSubDistrictId () {
        this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', {
          key: 'sub_district_id',
          value: this.selectedSubDistrictId
        })
        if (this.preCheckoutData.user_place) {
          this.preCheckoutData.user_place.sub_district_id = this.selectedSubDistrictId
        }
      },
      'preCheckoutData.user_place.has_pet': function (newVal, oldVal) {
        if (newVal === 0) {
          this.preCheckoutData.user_place.pet_remark = null
        }
      }
    },
    methods: {
      ...mapActions('user', ['saveUserProfile']),
      hasUnavailableServiceArea () {
        if (this.experience) {
          const provinceIds = this.addressProvinces.filter(addressProvince =>
            (addressProvince.province_id === this.experience.province_id)
          ).map(addressProvince => { return addressProvince.id })
          if (!provinceIds.includes(this.selectedAddressProvinceId)) {
            return true
          } else if (this.experience.unavailable_service_area) {
            for (const province of this.experience.unavailable_service_area.address_provinces) {
              if (province.id === this.selectedAddressProvinceId) {
                return true
              }
            }
            for (const district of this.experience.unavailable_service_area.districts) {
              if (district.id === this.selectedDistrictId) {
                return true
              }
            }
          }
        }
        return false
      },
      openUnavailableServiceAreaModal () {
        this.$refs.unavailableServiceAreaModal.openModal()
      },
      getFullAddress (userPlace) {
        return userPlaceHelpers.getFullAddress(userPlace)
      },
      formSubmit () {
        console.log('form submit')
      },
      updatePreCheckoutData (attribute, e) {
        const value = e.target.value
        const data = {key: attribute, value: value}
        this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', data)
        // Check user place data for updating
        const addressFields = ['address', 'remark', 'pet_remark']
        if (addressFields.indexOf(attribute) > -1 && this.preCheckoutData.user_place) {
          this.preCheckoutData.user_place[attribute] = value
        }
      },
      updatePreBookingData (attribute, e) {
        const data = {key: attribute, value: e.target.value}
        this.$store.commit('booking/UPDATE_PRE_BOOKING_DATA', data)
      },
      scrollTo (offsetTop) {
        window.scrollTo(0, offsetTop - 100)
      },
      showMoreRemark () {
        this.$refs.remark.innerHTML = this.preCheckoutData.remark
      },
      formatPhoneNumber () {
        const tel = this.$refs.tel.value
        if (tel && tel.substring(0, 1) === '0') {
          const parsed = parse(tel, 'TH')
          const phone = format(parsed.phone, 'TH', 'International')
          const formattedPhone = phone.replace(/ /g, '')
          this.$refs.tel.value = formattedPhone
          const data = {
            key: 'tel',
            value: formattedPhone
          }
          this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', data)
        }
      },
      contactValidation () {
        let result = true
        if (this.isEditing) {
          this.formatPhoneNumber()
          const emailFormat = new RegExp('^([a-z0-9\\+_\\-]+)(\\.[a-z0-9\\+_\\-]+)*@([a-z0-9\\-]+\\.)+[a-z]{2,6}$')
          if (!this.$refs.name.value) {
            this.$refs.name.focus()
            this.scrollTo(this.$refs.name.offsetTop)
            result = false
          } else if (!this.user && !this.$refs.email.value) {
            this.$refs.email.focus()
            this.scrollTo(this.$refs.email.offsetTop)
            result = false
          } else if (!this.user && this.$refs.email.value && !emailFormat.test(this.$refs.email.value)) {
            this.$refs.email.value = ''
            this.$refs.email.focus()
            this.scrollTo(this.$refs.email.offsetTop)
            result = false
          } else if (!this.$refs.tel.value) {
            this.$refs.tel.focus()
            this.scrollTo(this.$refs.tel.offsetTop)
            result = false
          } else if (this.$refs.tel.value && !isValidNumber(this.$refs.tel.value)) {
            this.$refs.tel.focus()
            this.scrollTo(this.$refs.tel.offsetTop)
            result = false
          } else if (!this.$refs.address.value) {
            this.$refs.address.focus()
            this.scrollTo(this.$refs.address.offsetTop)
            result = false
          } else {
            result = true
          }
        }
        return result
      },
      openSavePlaceModal () {
        const latitude = this.preCheckoutData.user_place.latitude ? Number(this.preCheckoutData.user_place.latitude) : null
        const longitude = this.preCheckoutData.user_place.longitude ? Number(this.preCheckoutData.user_place.longitude) : null
        if (this.preCheckoutData.user_place && (!latitude || !longitude)) {
          this.$validator.errors.add({ field: 'location', msg: 'no location', scope: 'form-address' })
        } else {
          this.$validator.errors.remove({ field: 'location', scope: 'form-address' })
        }
        this.$validator.validateAll('form-address').then(async (valid) => {
          if (valid) {
            this.formatPhoneNumber()
            const submitData = {
              user_id: this.user.id,
              tel: this.$refs.tel.value
            }
            await this.saveUserProfile(submitData)
            this.$refs.savePlaceModal.showModal(true)
          }
        })
      },
      openPinlocationModal () {
        const provinceId = this.preBookingData.province_id
        let lat = this.globalState.citiesLocations[provinceId].lat
        let lng = this.globalState.citiesLocations[provinceId].lng
        let isInit = true
        const latitude = this.preCheckoutData.user_place.latitude ? Number(this.preCheckoutData.user_place.latitude) : null
        const longitude = this.preCheckoutData.user_place.longitude ? Number(this.preCheckoutData.user_place.longitude) : null
        if (this.preCheckoutData && this.preCheckoutData.user_place && latitude && longitude) {
          lat = latitude
          lng = longitude
          isInit = false
        }
        this.$refs.pinLocationModal.show(lat, lng, isInit)
      },
      isEmptyFormData () {
        let isEmpty = true
        if (this.preCheckoutData.name && this.preCheckoutData.tel && this.preCheckoutData.address) {
          isEmpty = false
        }
        // Check has_pet === null only
        if (this.preCheckoutData.user_place && this.preCheckoutData.user_place.has_pet === null) {
          isEmpty = true
        }
        return isEmpty
      },
      clearLocationError () {
        this.$validator.errors.remove('location', 'form-address')
        this.$emit('savedLocation', true)
      },
      saveMyPlace () {
        const latitude = this.preCheckoutData.user_place.latitude ? Number(this.preCheckoutData.user_place.latitude) : null
        const longitude = this.preCheckoutData.user_place.longitude ? Number(this.preCheckoutData.user_place.longitude) : null
        if (this.preCheckoutData.user_place && (!latitude || !longitude)) {
          this.$validator.errors.add({ field: 'location', msg: 'no location', scope: 'form-address' })
        } else {
          this.$validator.errors.remove({ field: 'location', scope: 'form-address' })
        }
        this.$validator.validateAll('form-address').then(async (valid) => {
          if (valid) {
            this.formatPhoneNumber()
            const submitData = {
              user_id: this.user.id,
              tel: this.$refs.tel.value
            }
            await this.saveUserProfile(submitData)
            this.setIsEditing(false)
            if (this.preCheckoutData.user_place) {
              this.$store.dispatch('listing/UPDATE_USER_PLACE', this.preCheckoutData.user_place)
            }
          }
        })
      },
      setIsEditing (status) {
        this.isEditing = status
      },
      fetchAddressProvinces () {
        this.$store.dispatch('addressProvince/LOAD_ADDRESS_PROVINCES')
      },
      fetchDistricts (provinceId) {
        if (provinceId) {
          const queryData = {
            province_id: provinceId
          }
          this.$store.dispatch('addressProvince/LOAD_DISTRICTS', queryData)
        }
      },
      fetchSubDistricts (districtId) {
        if (districtId) {
          const queryData = {
            district_id: districtId
          }
          this.$store.dispatch('addressProvince/LOAD_SUB_DISTRICTS', queryData)
        }
      }
    },
    mounted () {
      if (this.isEmptyFormData()) {
        this.setIsEditing(true)
      }
      if (this.userPartner && !this.preBookingData.user_place_id) {
        const address = (this.userPartner.origin_address) ? this.userPartner.origin_address : this.userPartner.destination_address
        const data = {key: 'address', value: address}
        this.$store.commit('booking/UPDATE_PRE_CHECKOUT_DATA', data)
      }
      this.fetchAddressProvinces()
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/booking/components/ContactForm.vue